import { Theme } from "@deere/fuel-react";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { List, ListItem, SxProps, Tooltip, Typography } from "@mui/material";
import { gridClasses } from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";
import { getMessages } from "./Summary/components/LineSummary/utils/tableUtils";
import { LineStatus } from "./types";

export const HELD_FOR_FUTURE_RELEASE_STATUS_CODE = "L031";

export const generateGridDefaultStyles = (theme: Theme): SxProps => ({
  border: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  [`.${gridClasses.overlayWrapper}`]: {
    height: "50vh",
  },
  [`.${gridClasses.overlayWrapperInner}`]: {
    height: "100% !important",
  },
  [`.${gridClasses.virtualScroller}`]: {
    overflow: "hidden",
    border: 0,
    overflowX: "scroll",
  },
  [`.${gridClasses.cell}`]: {
    padding: theme.tokens.FuelSpacing4,
    outline: "none !important",
    display: "flex",
    alignItems: "center",
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
    "& a svg": {
      marginTop: "auto",
      marginBottom: "auto",
    },
  },
  [`& .${gridClasses.row}`]: {
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
    "&:last-child": {
      borderBottom: 0,
    },
    "& > .MuiDataGrid-cell:first-of-type": {
      padding: "16px 4px",
    },
  },
  [`& .${gridClasses.toolbarContainer}, & .${gridClasses["container--top"]}`]: {
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
    borderRadius: 0,
  },
  [`& .${gridClasses.columnHeader}`]: {
    paddingLeft: theme.tokens.FuelSpacing4,
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
  },
  [`& .${gridClasses.columnSeparator}`]: {
    color: "transparent",
    transition: "color 0.2s",
  },
  [`& .${gridClasses.columnHeaders}:hover .${gridClasses.columnSeparator}`]: {
    color: theme.tokens.FuelColorBorderLight,
  },
  [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
    backgroundColor: theme.tokens.FuelColorBackgroundWhite,
  },
  "& .MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
    padding: 0,
  },
});

export const getInvestigationIconForAllOrders = (
  lineStatuses: LineStatus[] = [],
  creditCheckNotOk: boolean,
  theme: Theme,
  t: TFunction
) => {
  const investigations = [
    ...lineStatuses,
    ...(creditCheckNotOk &&
    !lineStatuses?.includes(LineStatus.DEALER_INVESTIGATION)
      ? [LineStatus.DEALER_INVESTIGATION]
      : []),
  ];
  return (
    <>
      {investigations.map((status: string) =>
        getInvestigationIconForLines(status, creditCheckNotOk, theme, t)
      )}
    </>
  );
};

export const getInvestigationIconForLines = (
  investigation: string,
  creditCheckNotOk: boolean,
  theme: Theme,
  t: TFunction
) => {
  const dealerInvestigationTooltipMsg = creditCheckNotOk
    ? "orders:table.dealerInvestigationCreditCheckNotOk.tooltip"
    : "orders:table.dealerInvestigation.tooltip";

  if (investigation === LineStatus.DEERE_INVESTIGATION.toString()) {
    return (
      <Tooltip
        key="deere-investigation"
        title={t("orders:table.deereInvestigation.tooltip")}
      >
        <InfoIcon
          data-testid="deere-investigation-icon"
          sx={{ color: theme.tokens.FuelColorAlertInfoIcon }}
        />
      </Tooltip>
    );
  }

  if (investigation === LineStatus.DEALER_INVESTIGATION.toString()) {
    return (
      <Tooltip
        key="dealer-investigation"
        title={t(dealerInvestigationTooltipMsg)}
      >
        <WarningIcon
          data-testid="dealer-investigation-icon"
          sx={{ color: theme.tokens.FuelColorAlertWarningIcon }}
        />
      </Tooltip>
    );
  }

  return <></>;
};

export const getInvestigationTooltip = (
  investigation: string,
  creditCheckNotOk: boolean,
  t: TFunction
) => {
  const dealerInvestigationTooltipMsg = creditCheckNotOk
    ? "orders:table.dealerInvestigationCreditCheckNotOk.tooltip"
    : "orders:table.dealerInvestigation.tooltip";
  if (investigation === LineStatus.DEERE_INVESTIGATION.toString()) {
    return t("orders:table.deereInvestigation.tooltip");
  }
  if (investigation === LineStatus.DEALER_INVESTIGATION)
    return t(dealerInvestigationTooltipMsg);
  if (investigation === HELD_FOR_FUTURE_RELEASE_STATUS_CODE) {
    return t(
      `orders:orderLines.warning.description.${HELD_FOR_FUTURE_RELEASE_STATUS_CODE}`
    );
  }
  return "";
};

export const getInvestigationLineItemTooltip = (
  investigation: string,
  lineStatusCodes: string[] | undefined,
  theme: Theme,
  t: TFunction
) => {
  const investigationTitle = (() => {
    switch (investigation) {
      case LineStatus.DEALER_INVESTIGATION.toString():
        return t("orderLines.warning.description.title.dealerInvestigation");
      case LineStatus.DEERE_INVESTIGATION.toString():
        return t("orderLines.warning.description.title.deereInvestigation");
      default:
        return "";
    }
  })();

  const messages = getMessages(lineStatusCodes ?? [], t);

  return (
    <>
      <Typography
        sx={{
          padding: 0,
          paddingBottom: 0,
          paddingTop: 0,
          fontSize: theme.tokens.FuelFontSizeHeading6,
          fontWeight: "400",
          lineHeight: "16px",
        }}
      >
        {messages.length > 0 ? `${investigationTitle}:` : investigationTitle}
      </Typography>
      <List
        style={{
          listStyleType: "disc",
          listStylePosition: "inside",
          paddingBottom: 0,
          paddingTop: 0,
        }}
      >
        {messages.map((message) => (
          <ListItem
            sx={{
              display: "list-item",
              paddingBottom: 0,
              paddingTop: 0,
              fontSize: theme.tokens.FuelFontSizeHeading6,
              fontWeight: "400",
              lineHeight: "16px",
            }}
            key={message}
          >
            {message}
          </ListItem>
        ))}
      </List>
    </>
  );
};

export const getInvestigationAllOrdersTooltip = (
  lineStatuses: string[] = [],
  creditCheckNotOk: boolean,
  t: TFunction
) => {
  if (creditCheckNotOk) {
    return (
      <>{t("orders:table.dealerInvestigationCreditCheckNotOk.tooltip")} </>
    );
  }
  if (lineStatuses?.includes(LineStatus.DEALER_INVESTIGATION)) {
    return <>{t("orders:table.dealerInvestigation.tooltip")} </>;
  }
  if (lineStatuses?.includes(LineStatus.DEERE_INVESTIGATION)) {
    return <>{t("orders:table.deereInvestigation.tooltip")} </>;
  }
  if (lineStatuses?.includes(HELD_FOR_FUTURE_RELEASE_STATUS_CODE)) {
    return (
      <>
        {t(
          `orders:orderLines.warning.description.${HELD_FOR_FUTURE_RELEASE_STATUS_CODE}`
        )}{" "}
      </>
    );
  }
  return "";
};
